"use client";

import React, {Fragment} from "react";
import {Popover, Transition} from "@headlessui/react";
import {MdManageAccounts} from "react-icons/md";
import {ToggleIconField} from "~/components/forms/ToggleIconField";
import {useClientAuth} from "~/components/providers/client/AuthProvider";
import {useObfuscation} from "~/components/obfuscation/ObfuscationProvider";

export type AdminMenuDictionary = {
    adminMenuButtonLabel: string;
    adminMenuTitle: string;
    adminMenuObfuscationLabel: string;
    adminMenuObfuscationDescription: string;
};

export function AdminMenu({
    dictionary,
}: {
    dictionary: AdminMenuDictionary;
}) {
    const {hasPermAdmin, isUserReady} = useClientAuth();
    const {isObfuscationEnabled, setObfuscationEnabled} = useObfuscation();

    if (!isUserReady || !hasPermAdmin) return null;

    return (
        <div className="pointer-events-none fixed inset-0 z-1003">
            <Popover as="div" className="pointer-events-auto absolute bottom-4 right-4 font-jost sm:bottom-6 sm:right-6">
                {({open}) => (
                    <>
                        <Transition
                            as={Fragment}
                            show={open}
                            enter="transition ease-out duration-150"
                            enterFrom="translate-y-2 opacity-0"
                            enterTo="translate-y-0 opacity-100"
                            leave="transition ease-in duration-100"
                            leaveFrom="translate-y-0 opacity-100"
                            leaveTo="translate-y-2 opacity-0"
                        >
                            <Popover.Panel className="absolute bottom-full right-0 mb-3 w-[min(22rem,calc(100vw-2rem))] rounded-lg bg-white p-5 text-zinc-900 shadow-xl outline outline-1 outline-zinc-200">
                                <h2 className="text-lg font-semibold leading-tight">
                                    {dictionary.adminMenuTitle}
                                </h2>
                                <div className="mt-4 border-t border-zinc-200 pt-4">
                                    <ToggleIconField
                                        checked={isObfuscationEnabled}
                                        label={dictionary.adminMenuObfuscationLabel}
                                        labelClassName="font-semibold text-zinc-900"
                                        setValueAction={setObfuscationEnabled}
                                    />
                                    <p className="mt-2 text-sm leading-snug text-zinc-600">
                                        {dictionary.adminMenuObfuscationDescription}
                                    </p>
                                </div>
                            </Popover.Panel>
                        </Transition>
                        <Popover.Button
                            aria-label={dictionary.adminMenuButtonLabel}
                            title={dictionary.adminMenuButtonLabel}
                            className="flex size-12 items-center justify-center rounded-md bg-zinc-800 text-white shadow-lg outline outline-1 outline-white/40 transition-colors hover:bg-zinc-700 focus:outline-hidden focus:ring-4 ring-color"
                        >
                            <MdManageAccounts aria-hidden="true" className="size-7" />
                        </Popover.Button>
                    </>
                )}
            </Popover>
        </div>
    );
}
